import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import clsx from 'clsx';

import { useAppSelector } from 'redux/store';
import { NewsOverviewFieldsFragment } from 'api/graphql-types';

import { FilterButton, IFilterItem } from 'components/filter-button/filter-button';
import { CardNews, CardNewsType } from 'components/card';
import { EmptyState } from 'components/empty-state/empty-state';

import styles from './news-overview.module.scss';
import { NumberParam, useQueryParams } from 'use-query-params';
import { NEWS_ARTICLES_OVERVIEW_LIMIT } from './data';
import { searchNewsArticles } from 'api/algolia';
import useNewsFilters from './data/use-news-filter';
import { Button } from 'components/button';
import { getSite } from 'utils/subsite';
import { NewsAlgoliaFragment } from 'redux/stores/news';
import { PAGE_QUERY_KEY, THEME_QUERY_KEY, ThemeArrayParam, usePagination, useTheme } from 'utils/hooks/queries';

type FetchNewsArticles = (page: number, themeIds: string[]) => Promise<void>;

type NewsArticlesFetchProps = {
	page: number;
	themeIds: string[];
};

export interface INewsOverviewProps {
	module: NewsOverviewFieldsFragment;
}

const NewsOverview = (props: INewsOverviewProps): React.JSX.Element => {
	const router = useRouter();
	const { formatMessage } = useIntl();

	const [query, setQuery] = useQueryParams({
		[PAGE_QUERY_KEY]: NumberParam,
		[THEME_QUERY_KEY]: ThemeArrayParam
	});

	const overviewRef = useRef<HTMLDivElement>(null);
	const { newsArticles, newsArticlesCount } = useAppSelector((state) => state.news);
	const { contentType, isSubsite } = useAppSelector((state) => state.settings);

	const [requests, setRequests] = useState<NewsAlgoliaFragment[]>(newsArticles || []);
	const [requestIsLoading, setRequestIsLoading] = useState<boolean>(false);
	const [requestCount, setRequestCount] = useState<number>(newsArticlesCount);

	const [initialQuery, setInitialQuery] = useState<typeof query | undefined>();
	const [hasFiltered, setHasFiltered] = useState<boolean>(false);

	const { currentPage, setCurrentPage, Pagination } = usePagination(NEWS_ARTICLES_OVERVIEW_LIMIT);
	const { selectedThemes, setSelectedThemesParam } = useTheme();

	const fetchNewsArticles: FetchNewsArticles = async (page, themeIds): Promise<void> => {
		setRequestIsLoading(true);

		try {
			const { hostname } = window.location;
			const site = getSite(hostname);

			const { results, total } = await searchNewsArticles(
				'',
				{
					themesIds: themeIds
				},
				{
					limit: NEWS_ARTICLES_OVERVIEW_LIMIT,
					page,
					sort: 'desc',
					locale: router.locale
				},
				site as any
			);

			setRequests(results);
			setRequestCount(total);
		} catch {
			setRequests([]);
			setRequestCount(0);
		}

		setRequestIsLoading(false);
	};

	const scrollToTop = (): void => {
		if (typeof window !== 'undefined') {
			const extraOffset = 140;
			const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.scrollY - extraOffset;
			window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
		}
	};

	const handleDeleteFilter = (): void => {
		setQuery({
			thema: null,
			pagina: null
		});
		setTempSelectedThemes([]);
		handleFetchNewsArticles(undefined, true);
	};

	const handlePageChange = (page: number): void => {
		if (page !== currentPage) {
			setCurrentPage(page);
			handleFetchNewsArticles({ page });
		}
	};

	const handleFetchNewsArticles = async (data?: Partial<NewsArticlesFetchProps>, reset?: boolean): Promise<void> => {
		const selectedPage = reset ? 1 : data?.page || currentPage;

		const selectedThemeIds = reset ? [] : data?.themeIds || selectedThemes.map((theme) => String(theme.id));

		scrollToTop();
		setHasFiltered(data !== undefined);
		fetchNewsArticles(selectedPage, selectedThemeIds);
	};

	const newsArticlesFilters = useNewsFilters(props.module, (themeIds: string[]) =>
		handleFetchNewsArticles({ page: 1, themeIds })
	);

	useEffect(() => {
		handleFetchNewsArticles(undefined);
		setTempSelectedThemes(selectedThemes.map((theme) => ({ id: theme.id, label: theme.label })));
	}, [initialQuery]);

	useEffect(() => {
		if (!hasFiltered && !initialQuery && query[THEME_QUERY_KEY]) {
			setInitialQuery(query);
		}
	}, [query]);

	const getNewsUrl = (): CardNewsType => {
		if (contentType === 'op-oost-news-overview' || contentType === 'regio-expres-news-overview') {
			return 'nieuwsberichten';
		}
		return 'nieuws';
	};

	const [tempSelectedThemes, setTempSelectedThemes] = useState<IFilterItem[]>([]);

	const applyFilters = (): void => {
		setSelectedThemesParam(tempSelectedThemes.length > 0 ? tempSelectedThemes : null);
		handleFetchNewsArticles({ page: 1, themeIds: tempSelectedThemes.map((theme) => String(theme.id)) });
	};

	return (
		<div className={styles.NewsOverview} ref={overviewRef}>
			<div className={styles.wrapper}>
				<div className={clsx(styles.filterBar, isSubsite && styles.justifyRight)}>
					{!isSubsite && (
						<div className={styles.filterContent}>
							<span className={styles.label}>{props.module.filterLabel}</span>
							<div className={styles.filterButtons}>
								<span className={styles.themeFilter}>
									<FilterButton
										title={formatMessage({ id: 'modules.newsOverview.themeFilter.title' })}
										items={newsArticlesFilters[0].options as IFilterItem[]}
										onChange={applyFilters}
										onSelectedItemsChanged={setTempSelectedThemes}
										selectedItems={tempSelectedThemes}
									/>
								</span>
								{!!tempSelectedThemes.length && (
									<span className={styles.removeFilters}>
										<Button
											title={formatMessage(
												{ id: 'modules.newsOverview.deleteFilters' },
												{ amount: tempSelectedThemes.length }
											)}
											variant="tertiary-dark"
											onClick={handleDeleteFilter}
										/>
									</span>
								)}
							</div>
						</div>
					)}

					<div className={styles.amountOfResults}>
						{formatMessage({ id: 'global.results' }, { results: requestCount })}
					</div>
				</div>
				<div
					className={clsx(styles.content, requestIsLoading && styles.resultsLoading)}
					style={{
						opacity: requestIsLoading ? '0.2' : 1,
						height: requests?.length === 9 ? '1350px' : '300px'
					}}
				>
					{requests?.length > 0 ? (
						requests?.map((article, index) => (
							<div key={index}>
								<CardNews
									id={`news-overview-${index}`}
									bodyText={article?.bodyText?.bodyText || ''}
									excerpt={article?.header?.excerpt || ''}
									publishedAt={article?.firstPublishedAt}
									slug={article?.slug || ''}
									title={article?.title || ''}
									cover={article.header.cover}
									key={index}
									type={getNewsUrl()}
									priority={index < 3}
								/>
							</div>
						))
					) : (
						<div className={styles.emptyState}>
							<EmptyState onClearFilters={handleDeleteFilter} isLoading={requestIsLoading} />
						</div>
					)}
				</div>
			</div>
			<div className={styles.pagination}>
				<Pagination totalCount={requestCount} onPageChange={handlePageChange} />
			</div>
		</div>
	);
};

export default NewsOverview;
